import {HttpClient, HttpResponse} from '@angular/common/http';
import {Inject, Injectable, makeStateKey, PLATFORM_ID, StateKey, TransferState} from '@angular/core';
import {Observable} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {ApiHelper} from './api.helper';
import {WPPostGuide} from '../types/WPPostGuide';
import {WPPostGuideLink} from '../types/WPPostGuideLink';
import {ToolsService} from './tools.service';
import {AppConfigService} from './app-config.service';
import {WPTag} from '../types/WPTag';
import {WPGuideAggregated} from '../types/WPGuideAggregated';
import {TransferStateKeyEnum} from '../enums/TransferStateKeyEnum';
import {isPlatformBrowser, isPlatformServer} from '@angular/common';

@Injectable()
export class StaticGuideService {
  constructor(
    private http: HttpClient,
    private apiHelper: ApiHelper,
    private tools: ToolsService,
    private appConfigService: AppConfigService,
    private transferState: TransferState,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  public GetGuideById(id: number): Observable<WPPostGuide> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guide-${id}.json`;

    return this.http.get<WPPostGuide>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetGuideBySlug(slug: string): Observable<WPPostGuide> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guide-${slug}.json`;

    return this.http.get<WPPostGuide>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetGuideLinksByGuideId(id: number): Observable<WPPostGuideLink[]> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guidelink-${id}.json`;

    return this.http.get<WPPostGuideLink[]>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetGuideLatestResponse(
    page: number,
    tag: number,
    excludedIds?: number[],
  ): Observable<HttpResponse<WPPostGuide[]> & {totalPages: number; total: number}> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guide-${tag}-page-${page}.json`;

    const stateKey: StateKey<{totalPages: number; total: number}> = makeStateKey(
      `${TransferStateKeyEnum.GUIDE_RESPONSE_DATA}_${tag}`,
    );

    if (isPlatformBrowser(this.platformId)) {
      const cachedData = this.transferState.get(stateKey, null);
      if (cachedData) {
        this.transferState.remove(stateKey);

        return this.http.get<WPPostGuide[]>(endpoint).pipe(
          map((posts) => {
            let filteredPosts = [...posts];
            if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
              filteredPosts = filteredPosts.filter((post) => !excludedIds.includes(post.id));
            }

            return {
              body: filteredPosts,
              totalPages: cachedData.totalPages,
              total: cachedData.total,
            } as HttpResponse<WPPostGuide[]> & {totalPages: number; total: number};
          }),
          catchError((error) => this.apiHelper.handleError(error)),
        );
      }
    }
    let totalPages = 1;
    let total = 0;

    return this.http.get<WPPostGuide[]>(endpoint, {observe: 'response'}).pipe(
      map((response) => {
        const totalPagesHeader = response.headers.get('x-amz-meta-x-total-pages');
        if (totalPagesHeader) {
          totalPages = parseInt(totalPagesHeader, 10);
        }

        const totalHeader = response.headers.get('x-amz-meta-x-total');
        if (totalHeader) {
          total = parseInt(totalHeader, 10);
        }

        let filteredBody = [...response.body];
        if (!this.tools.isNil(excludedIds) && excludedIds.length > 0) {
          filteredBody = filteredBody.filter((post) => !excludedIds.includes(post.id));
        }

        if (isPlatformServer(this.platformId)) {
          this.transferState.set(stateKey, {totalPages, total});
        }

        const enhancedResponse = {
          ...response,
          body: filteredBody,
          totalPages,
          total,
        } as HttpResponse<WPPostGuide[]> & {totalPages: number; total: number};

        return enhancedResponse;
      }),
      catchError((error) => this.apiHelper.handleError(error)),
    );
  }

  public GetGuideTags(): Observable<WPTag[]> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guidetag.json`;

    return this.http.get<WPTag[]>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }

  public GetGuideAggregated(): Observable<WPGuideAggregated> {
    const apiUrl: string = this.appConfigService.appConfig.amazonS3Url;
    const endpoint = `${apiUrl}wp/guide/guide-aggregated.json`;

    return this.http.get<WPGuideAggregated>(endpoint).pipe(catchError((error) => this.apiHelper.handleError(error)));
  }
}
